'use client';

import { Upload, Zap, RefreshCw, Shield } from 'lucide-react';

interface WelcomeScreenProps {
  onStartTour: () => void;
  onSkip: () => void;
}

const FEATURES = [
  {
    icon: Upload,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    name: 'CSV / Excel Import',
    description: 'Upload spreadsheets and map columns to your monday.com boards with AI assistance.',
    tier: 'Free & unlimited',
  },
  {
    icon: Zap,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    name: 'Bulk Operations',
    description: 'Update, delete, or modify hundreds of items at once with safety previews.',
    tier: '5 free / mo',
  },
  {
    icon: RefreshCw,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    name: 'Recurring Tasks',
    description: 'Automatically create items on a schedule — daily, weekly, or custom.',
    tier: '10 free / mo',
  },
];

export function WelcomeScreen({ onStartTour, onSkip }: WelcomeScreenProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-bg)] px-4">
      <div className="max-w-lg w-full text-center">
        {/* Logo */}
        <div className="mb-6">
          <img src="/taskloops-logo.png" alt="TaskLoops" className="w-11 h-11 mx-auto rounded-xl" />
        </div>

        {/* Heading */}
        <h1 className="font-heading text-2xl font-bold text-[var(--text-heading)] mb-2">
          Welcome to TaskLoops
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mb-8">
          Safe bulk automation for monday.com — import, update, and schedule with confidence.
        </p>

        {/* Feature Cards */}
        <div className="space-y-3 mb-6 text-left">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.name}
                className="flex items-center gap-4 p-4 rounded-lg bg-[var(--surface-card)] border border-[var(--border-default)]"
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${feature.iconBg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${feature.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--text-heading)]">{feature.name}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{feature.description}</div>
                </div>
                <span className="flex-shrink-0 text-xs text-[var(--text-muted)] whitespace-nowrap">
                  {feature.tier}
                </span>
              </div>
            );
          })}
        </div>

        {/* Safety Callout */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-200 mb-8 text-left">
          <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">
            Every operation has a safety net — preview changes before they run, and roll back if anything looks wrong.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onStartTour}
            className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
          >
            Quick tour — 1 min
          </button>
          <button
            onClick={onSkip}
            className="px-6 py-2.5 border border-[var(--border-default)] text-[var(--text-body)] rounded-lg hover:bg-[var(--surface-hover)] transition-colors font-medium text-sm"
          >
            Skip to app
          </button>
        </div>
      </div>
    </div>
  );
}
